import { formatCurrency, formatPriceDisplay } from '@/lib/utils/format';
import { HStack, VStack, Text, Box } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { memo } from 'react';

export interface UserFill {
  closedPnl: string;
  coin: string;
  crossed: boolean;
  dir: string;
  hash: string;
  oid: number;
  px: string;
  side: string;
  startPosition: string;
  sz: string;
  time: number;
  tid: number;
}

interface HistoryCardProps {
  fill: UserFill;
}

export const HistoryCard = memo(function HistoryCard({
  fill,
}: HistoryCardProps) {
  const t = useTranslations();

  // Calculate trade details directly without useMemo to avoid hooks issues
  const coin = fill.coin.replace('-PERP', '');
  const isLong = fill.side !== 'sell';
  const entryPrice = Number.parseFloat(fill.px);
  const size = Number.parseFloat(fill.sz);
  const sizeValue = entryPrice * size;

  return (
    <VStack flex={1} gap={1} align="start">
      <HStack w="full" align="center" gap={{ base: 2, md: 4 }} py={1}>
        {/* Left: Token and badges */}
        <VStack flex={{ base: 1, md: 0 }} align="flex-start" justify="center" gap={1}>
          <Text fontSize="xs" color="text.t1/80" fontWeight="medium">
            {coin}
          </Text>
          <HStack align="center" justify="flex-start" gap={1}>
            {/* Long/Short badge */}
            <Box
              h={5}
              px={1.5}
              py={0.5}
              rounded="md"
              display="flex"
              justifyContent="center"
              alignItems="center"
              bg={isLong ? 'profit/15' : 'loss/15'}
            >
              <Text
                color={isLong ? 'profit' : 'loss'}
                fontSize="0.6875rem"
                fontWeight="medium"
              >
                {isLong ? t('common.buy') : t('common.sell')}
              </Text>
            </Box>
          </HStack>
        </VStack>
        {/* Center: Entry Price */}
        <VStack flex={{ base: 1, md: 2 }} align="flex-start" justify={{ base: 'center', md: 'flex-start' }} gap={0.5}>
          <Text fontSize="xs" color="text.t1/40" fontWeight="medium">
            {t('trader.history.entryPrice')}
          </Text>
          <Text fontSize="sm" color="text.t1/80" fontWeight="medium">
            {formatPriceDisplay(entryPrice)}
          </Text>
        </VStack>
        {/* Right: Size */}
        <VStack flex={1} align="flex-start" justify="center" gap={0.5}>
          <Text fontSize="xs" color="text.t1/40" fontWeight="medium">
            {t('trader.history.value')}
          </Text>
          <Text fontSize="sm" color="text.t1/80" fontWeight="medium">
            {formatCurrency(sizeValue, { maximumFractionDigits: 2 })}
          </Text>
        </VStack>
      </HStack>
      <Text
        fontSize="12px"
        fontStyle="italic"
        color={isLong ? 'profit' : 'loss'}
        fontWeight="medium"
      >
        {fill.dir}
      </Text>
    </VStack>
  );
});
